import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
const apiCache = new Map();
const TOKEN_STORAGE_KEY = 'sedyam_token';
const PROFILE_STORAGE_KEY = 'sedyam_profile';

const getSessionCacheScope = () => {
  if (typeof window === 'undefined') return 'server';

  try {
    const token = window.localStorage.getItem(TOKEN_STORAGE_KEY) || '';
    const rawProfile = window.localStorage.getItem(PROFILE_STORAGE_KEY) || '';
    let profile = null;
    try {
      profile = rawProfile ? JSON.parse(rawProfile) : null;
    } catch {
      profile = null;
    }

    const role = String(profile?.role || 'guest');
    const id = String(profile?.id || profile?._id || '');
    const district = String(profile?.district || '');
    const tokenTail = token ? token.slice(-12) : 'no-token';
    return `${role}|${id}|${district}|${tokenTail}`;
  } catch {
    return 'session-unknown';
  }
};

const buildCacheKey = (apiFn, params, customKey, sessionScope) => {
  if (customKey) return customKey;

  try {
    return `${sessionScope}:${apiFn?.name || 'anonymous'}:${JSON.stringify(params ?? null)}`;
  } catch {
    return `${sessionScope}:${apiFn?.name || 'anonymous'}:${String(params ?? '')}`;
  }
};

const readCachedEntry = (key, staleTime) => {
  const cached = apiCache.get(key);
  if (!cached) return null;

  if (typeof staleTime === 'number' && staleTime >= 0 && Date.now() - cached.timestamp > staleTime) {
    apiCache.delete(key);
    return null;
  }

  return cached.data;
};

export function useApi(apiFn, params = null, options = {}) {
  const {
    pollInterval = null,
    cacheKey = '',
    staleTime = 30000,
    keepPreviousData = true,
    revalidateOnMount = true,
  } = options;
  const timerRef = useRef(null);
  const mountedRef = useRef(false);
  const requestIdRef = useRef(0);
  const paramsRef = useRef(params);
  const sessionScope = getSessionCacheScope();

  const resolvedCacheKey = useMemo(
    () => buildCacheKey(apiFn, params, cacheKey, sessionScope),
    [apiFn, cacheKey, params, sessionScope]
  );
  const cachedData = useMemo(
    () => readCachedEntry(resolvedCacheKey, staleTime),
    [resolvedCacheKey, staleTime]
  );

  const [data, setData] = useState(() => cachedData ?? null);
  const [loading, setLoading] = useState(() => cachedData == null);
  const [error, setError] = useState(null);
  const dataRef = useRef(cachedData ?? null);

  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const fetch = useCallback(
    async (silent = false) => {
      const requestId = ++requestIdRef.current;
      const hasData = dataRef.current !== null;

      if (!silent && (!keepPreviousData || !hasData)) {
        setLoading(true);
      }

      if (!silent) {
        setError(null);
      }

      try {
        const activeParams = paramsRef.current;
        const result = activeParams !== null ? await apiFn(activeParams) : await apiFn();

        if (!mountedRef.current || requestId !== requestIdRef.current) {
          return result;
        }

        const nextData = result?.data ?? result;
        apiCache.set(resolvedCacheKey, {
          data: nextData,
          timestamp: Date.now(),
        });
        dataRef.current = nextData;
        setData(nextData);
        setError(null);
        setLoading(false);
        return result;
      } catch (err) {
        if (!mountedRef.current || requestId !== requestIdRef.current) {
          return null;
        }

        if (!silent && !keepPreviousData) {
          dataRef.current = null;
          setData(null);
        }

        setError(err?.message || 'Request failed.');
        setLoading(false);
        throw err;
      }
    },
    [apiFn, keepPreviousData, resolvedCacheKey]
  );

  useEffect(() => {
    mountedRef.current = true;
    const hydratedData = readCachedEntry(resolvedCacheKey, staleTime);

    if (hydratedData !== null) {
      setData(hydratedData);
      setLoading(false);
      setError(null);
    } else if (!keepPreviousData) {
      setData(null);
      setLoading(true);
    }

    const shouldFetch = revalidateOnMount || hydratedData === null;
    if (shouldFetch) {
      fetch(hydratedData !== null).catch(() => {
        // Surface handled through state.
      });
    }

    if (pollInterval) {
      timerRef.current = window.setInterval(() => {
        fetch(true).catch(() => {
          // Preserve stale data when background refresh fails.
        });
      }, pollInterval);
    }

    return () => {
      mountedRef.current = false;
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [fetch, keepPreviousData, pollInterval, revalidateOnMount, resolvedCacheKey, staleTime]);

  return { data, loading, error, refetch: () => fetch() };
}
